import { Divider, Flex, VStack } from "@chakra-ui/react";
import { EmptyMessage } from "@components/EmptyMessage";
import { TokenMultichain } from "@components/Token";
import { ViewOverlay } from "@components/ViewOverlay/ViewOverlay";
import { getTokenId, getTokenSymbol, isMultichainToken, isTezosToken } from "@umami/chains";
import {
  useCurrentAccount,
  useGetNetworkChain,
  useGetTokensAndBalances,
  useIsSaplingEnabled,
} from "@umami/state";
import { isMultichainEnabled } from "@umami/utils";
import { useEffect, useRef, useState } from "react";

import { ShieldedBalanceToken } from "./ShieldedBalanceToken";
import { Token } from "./Token";
import { useBuyTokenFlow } from "../../hooks/useBuyTokenFlow";
import { shouldShowBuyButton } from "../../utils/tokenUtils";

export const Tokens = () => {
  const { balances, hasAssets, tokens } = useGetTokensAndBalances();
  const { handleAssetSelectClick } = useBuyTokenFlow();
  const [tokensWithBuyButton, setTokensWithBuyButton] = useState<Set<string>>(new Set());
  const isCheckingRef = useRef(false);
  const lastProcessedTokensRef = useRef<string>("");
  const getNetworkChain = useGetNetworkChain();
  const isSaplingEnabled = useIsSaplingEnabled();
  const currentAccount = useCurrentAccount();
  // Check which tokens should show buy buttons
  useEffect(() => {
    // Prevent multiple simultaneous executions
    if (isCheckingRef.current) {
      return;
    }

    // Create a stable key for the current tokens to avoid unnecessary re-runs
    const tokensKey = tokens
      .map(token => getTokenId(token))
      .sort()
      .join(",");

    // Skip if we've already processed these exact tokens
    if (lastProcessedTokensRef.current === tokensKey) {
      return;
    }

    isCheckingRef.current = true;
    lastProcessedTokensRef.current = tokensKey;

    const checkBuyButtons = async () => {
      try {
        const buyButtonTokens = new Set<string>();

        for (const token of tokens) {
          const symbol = getTokenSymbol(token);

          if (!symbol) {
            continue;
          }

          const tokenAddress = isMultichainToken(token) ? token.address : undefined;
          const shouldShow = await shouldShowBuyButton(
            getNetworkChain(token.chainId),
            symbol,
            tokenAddress
          );

          if (shouldShow) {
            buyButtonTokens.add(`${getTokenId(token)}|${token.chainId}`);
          }
        }

        setTokensWithBuyButton(buyButtonTokens);
      } finally {
        isCheckingRef.current = false;
      }
    };

    if (tokens.length > 0) {
      void checkBuyButtons();
    }
  }, [tokens, getNetworkChain]);

  const renderCoins = () => {
    if (isMultichainEnabled()) {
      const coinElements = balances.map(balance =>
        Number(balance.balance) > 0 ? (
          <TokenMultichain
            key={balance.chainId}
            balance={Number(balance.balance)}
            chain={balance.chainId}
            data-testid="multichain-coin"
            hasBuyButton
            hasSendButton
            hasShieldButton={isSaplingEnabled(balance.chainId)}
            name={balance.symbol}
            usdBalance={balance.totalUsdPrice}
            usdPrice={balance.usdPrice}
          />
        ) : null
      );

      // Add Shielded Balance token if available
      const shieldedBalanceElement = isSaplingEnabled() ? (
        <ShieldedBalanceToken key="shielded-balance" />
      ) : null;

      const coins = [shieldedBalanceElement, ...coinElements].filter(Boolean);

      if (currentAccount?.type === "ledger") {
        return coinElements;
      }

      return coins;
    }
  };

  const renderTokens = () =>
    tokens.map(token => {
      if (isMultichainEnabled()) {
        const tokenId = `${getTokenId(token)}|${token.chainId}`;
        const hasBuyButton = tokensWithBuyButton.has(tokenId);

        return (
          <TokenMultichain key={tokenId} hasBuyButton={hasBuyButton} hasSendButton token={token} />
        );
      } else if (isTezosToken(token)) {
        return <Token key={`${getTokenId(token)}|${token.chainId}`} token={token} />;
      }
    });

  return (
    <>
      <Flex
        zIndex={1}
        flexGrow={1}
        width="full"
        padding={{
          md: "10px",
          base: "8px",
        }}
      >
        {hasAssets ? (
          <VStack gap={{ base: "18px", md: "30px" }} width="full" divider={<Divider />} spacing="0">
            {renderCoins()}
            {renderTokens()}
          </VStack>
        ) : (
          <EmptyMessage
            cta="Buy crypto"
            onClick={handleAssetSelectClick}
            title="Add crypto to your portfolio"
          />
        )}
      </Flex>

      {!hasAssets && <ViewOverlay iconType="tokens" />}
    </>
  );
};
